import { createServerFn } from "@tanstack/react-start";
import { and, desc, eq, sql } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { subscribeEmailToNewsletter } from "../newsletter/subscribe.server";
import {
  buildNewsletterEmailHtml,
  craftStateToEmailHtml,
  htmlToPlainText,
  sanitizeEmailHtml,
} from "../email/render.server";
import { sendMail, verifySmtpConnection } from "../email/smtp.server";

async function requireNewsletterAdmin() {
  await requirePermission("newsletter", "send");
}

async function requireNewsletterSubscribersRead() {
  await requirePermission("insights", "read");
}

function campaignBodyHtml(campaign: {
  contentFormat: "html" | "craft";
  contentHtml: string | null;
  craftState: unknown;
}): string {
  if (campaign.contentFormat === "craft") {
    return craftStateToEmailHtml(campaign.craftState);
  }
  return sanitizeEmailHtml(campaign.contentHtml ?? "");
}

export const subscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator(z.object({ email: z.string().email(), source: z.string().optional() }))
  .handler(async ({ data }) => subscribeEmailToNewsletter(data.email, data.source ?? "insights_newsletter"));

export const unsubscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      token: z.string().min(1),
      reason: z.string().min(1).max(500),
    }),
  )
  .handler(async ({ data }) => {
    const db = getDb();
    const [row] = await db
      .select()
      .from(schema.newsletterSubscribers)
      .where(eq(schema.newsletterSubscribers.unsubscribeToken, data.token))
      .limit(1);
    if (!row) throw new Error("Invalid unsubscribe link");

    await db
      .update(schema.newsletterSubscribers)
      .set({
        status: "unsubscribed",
        optOutReason: data.reason.trim(),
        unsubscribedAt: new Date(),
        updatedAt: new Date(),
      })
      .where(eq(schema.newsletterSubscribers.id, row.id));

    return { ok: true, email: row.email.replace(/(.{2}).+(@.*)/, "$1***$2") };
  });

export const getUnsubscribeSubscriber = createServerFn({ method: "GET" })
  .inputValidator(z.object({ token: z.string().min(1) }))
  .handler(async ({ data }) => {
    const [row] = await getDb()
      .select({
        email: schema.newsletterSubscribers.email,
        status: schema.newsletterSubscribers.status,
      })
      .from(schema.newsletterSubscribers)
      .where(eq(schema.newsletterSubscribers.unsubscribeToken, data.token))
      .limit(1);
    if (!row) return null;
    return {
      maskedEmail: row.email.replace(/(.{2}).+(@.*)/, "$1***$2"),
      status: row.status,
    };
  });

export const listNewsletterSubscribers = createServerFn({ method: "GET" }).handler(async () => {
  await requireNewsletterSubscribersRead();
  return getDb()
    .select()
    .from(schema.newsletterSubscribers)
    .orderBy(desc(schema.newsletterSubscribers.createdAt));
});

export const listNewsletterCampaigns = createServerFn({ method: "GET" }).handler(async () => {
  await requireNewsletterAdmin();
  return getDb()
    .select()
    .from(schema.newsletterCampaigns)
    .orderBy(desc(schema.newsletterCampaigns.updatedAt));
});

export const getNewsletterCampaign = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requireNewsletterAdmin();
    const [row] = await getDb()
      .select()
      .from(schema.newsletterCampaigns)
      .where(eq(schema.newsletterCampaigns.id, data.id))
      .limit(1);
    return row ?? null;
  });

const campaignInput = z.object({
  subject: z.string().min(1),
  previewText: z.string().optional(),
  contentFormat: z.enum(["html", "craft"]).default("html"),
  contentHtml: z.string().optional(),
  craftState: z.union([z.string(), z.record(z.unknown())]).optional().nullable(),
  craftCustomCss: z.string().optional().nullable(),
});

export const createNewsletterCampaign = createServerFn({ method: "POST" })
  .inputValidator(campaignInput)
  .handler(async ({ data }) => {
    await requireNewsletterAdmin();
    const [row] = await getDb()
      .insert(schema.newsletterCampaigns)
      .values({
        subject: data.subject,
        previewText: data.previewText,
        contentFormat: data.contentFormat,
        contentHtml: data.contentHtml,
        craftState: typeof data.craftState === "string" ? JSON.parse(data.craftState) : data.craftState,
        craftCustomCss: data.craftCustomCss,
        status: "draft",
      })
      .returning();
    return row;
  });

export const updateNewsletterCampaign = createServerFn({ method: "POST" })
  .inputValidator(campaignInput.extend({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requireNewsletterAdmin();
    const db = getDb();
    const [existing] = await db
      .select()
      .from(schema.newsletterCampaigns)
      .where(eq(schema.newsletterCampaigns.id, data.id))
      .limit(1);
    if (!existing) throw new Error("Campaign not found");
    if (existing.status !== "draft" && existing.status !== "failed") {
      throw new Error("Only draft or failed campaigns can be edited");
    }

    const [row] = await db
      .update(schema.newsletterCampaigns)
      .set({
        subject: data.subject,
        previewText: data.previewText,
        contentFormat: data.contentFormat,
        contentHtml: data.contentHtml,
        craftState: typeof data.craftState === "string" ? JSON.parse(data.craftState) : data.craftState,
        craftCustomCss: data.craftCustomCss,
        updatedAt: new Date(),
      })
      .where(eq(schema.newsletterCampaigns.id, data.id))
      .returning();
    return row;
  });

export const deleteNewsletterCampaign = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requireNewsletterAdmin();
    const db = getDb();
    const [existing] = await db
      .select()
      .from(schema.newsletterCampaigns)
      .where(eq(schema.newsletterCampaigns.id, data.id))
      .limit(1);
    if (!existing) throw new Error("Campaign not found");
    if (existing.status === "sending") throw new Error("Campaign is currently sending");
    await db.delete(schema.newsletterCampaigns).where(eq(schema.newsletterCampaigns.id, data.id));
    return { ok: true };
  });

async function sendCampaignEmail(
  campaign: typeof schema.newsletterCampaigns.$inferSelect,
  to: string,
  options: { unsubscribeToken?: string; isTest?: boolean },
) {
  const bodyHtml = campaignBodyHtml(campaign);
  if (!bodyHtml.trim()) throw new Error("Campaign body is empty");

  const html = await buildNewsletterEmailHtml({
    bodyHtml,
    unsubscribeToken: options.unsubscribeToken,
    previewText: campaign.previewText ?? undefined,
  });

  await sendMail({
    to,
    subject: options.isTest ? `[TEST] ${campaign.subject}` : campaign.subject,
    html,
    text: htmlToPlainText(bodyHtml),
  });
}

export const sendTestNewsletter = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid(), email: z.string().email() }))
  .handler(async ({ data }) => {
    await requireNewsletterAdmin();
    const db = getDb();
    const [campaign] = await db
      .select()
      .from(schema.newsletterCampaigns)
      .where(eq(schema.newsletterCampaigns.id, data.id))
      .limit(1);
    if (!campaign) throw new Error("Campaign not found");

    try {
      await sendCampaignEmail(campaign, data.email, { isTest: true });
      await db.insert(schema.newsletterSends).values({
        campaignId: campaign.id,
        email: data.email,
        status: "sent",
        isTest: true,
      });
      return { ok: true };
    } catch (e) {
      const message = e instanceof Error ? e.message : "Send failed";
      await db.insert(schema.newsletterSends).values({
        campaignId: campaign.id,
        email: data.email,
        status: "failed",
        errorMessage: message,
        isTest: true,
      });
      throw new Error(message);
    }
  });

export const sendNewsletterCampaign = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requireNewsletterAdmin();
    const db = getDb();
    const [campaign] = await db
      .select()
      .from(schema.newsletterCampaigns)
      .where(eq(schema.newsletterCampaigns.id, data.id))
      .limit(1);
    if (!campaign) throw new Error("Campaign not found");
    if (campaign.status !== "draft" && campaign.status !== "failed") {
      throw new Error("Campaign has already been sent or is sending");
    }

    const subscribers = await db
      .select()
      .from(schema.newsletterSubscribers)
      .where(eq(schema.newsletterSubscribers.status, "subscribed"));

    await db
      .update(schema.newsletterCampaigns)
      .set({
        status: "sending",
        totalRecipients: subscribers.length,
        sentCount: 0,
        failedCount: 0,
        updatedAt: new Date(),
      })
      .where(eq(schema.newsletterCampaigns.id, campaign.id));

    let sentCount = 0;
    let failedCount = 0;

    for (const sub of subscribers) {
      try {
        await sendCampaignEmail(campaign, sub.email, { unsubscribeToken: sub.unsubscribeToken });
        sentCount += 1;
        await db.insert(schema.newsletterSends).values({
          campaignId: campaign.id,
          subscriberId: sub.id,
          email: sub.email,
          status: "sent",
        });
      } catch (e) {
        failedCount += 1;
        await db.insert(schema.newsletterSends).values({
          campaignId: campaign.id,
          subscriberId: sub.id,
          email: sub.email,
          status: "failed",
          errorMessage: e instanceof Error ? e.message : "Send failed",
        });
      }
    }

    await db
      .update(schema.newsletterCampaigns)
      .set({
        status: failedCount === subscribers.length && subscribers.length > 0 ? "failed" : "sent",
        sentCount,
        failedCount,
        sentAt: new Date(),
        updatedAt: new Date(),
      })
      .where(eq(schema.newsletterCampaigns.id, campaign.id));

    return { ok: true, sentCount, failedCount, totalRecipients: subscribers.length };
  });

export const testSmtpSettings = createServerFn({ method: "POST" }).handler(async () => {
  await requirePermission("settings", "manage_site");
  await verifySmtpConnection();
  return { ok: true };
});

export const exportNewsletterSubscribersCsv = createServerFn({ method: "GET" }).handler(async () => {
  await requireNewsletterSubscribersRead();
  const rows = await getDb()
    .select()
    .from(schema.newsletterSubscribers)
    .orderBy(desc(schema.newsletterSubscribers.createdAt));
  const header = "email,status,source,subscribed_at,unsubscribed_at,opt_out_reason";
  const lines = rows.map((r) =>
    [r.email, r.status, r.source, r.subscribedAt?.toISOString(), r.unsubscribedAt?.toISOString() ?? "", r.optOutReason ?? ""]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(","),
  );
  return { csv: [header, ...lines].join("\n") };
});

export const getNewsletterStats = createServerFn({ method: "GET" }).handler(async () => {
  await requireNewsletterSubscribersRead();
  const db = getDb();
  const [{ subscribed }] = await db
    .select({ subscribed: sql<number>`count(*)::int` })
    .from(schema.newsletterSubscribers)
    .where(eq(schema.newsletterSubscribers.status, "subscribed"));
  const [{ unsubscribed }] = await db
    .select({ unsubscribed: sql<number>`count(*)::int` })
    .from(schema.newsletterSubscribers)
    .where(eq(schema.newsletterSubscribers.status, "unsubscribed"));
  return { subscribed, unsubscribed };
});
